import { useTranslation } from 'react-i18next';
import {
    FileUploadItem,
    FileUploadStatus,
    isFileUploadItemAwaitingBackendTerminal,
} from '@/types/source-creation';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { StatusDrawerLayout } from './StatusDrawerLayout';
import { FileSourceStatusDrawerItem } from './FileSourceStatusDrawerItem';

interface FileUploadDrawerContentProps {
    fileItems: FileUploadItem[];
    isExpanded: boolean;
    setExpanded: (val: boolean) => void;
    onClear: () => void;
    onRetry: (itemId: string) => void;
}

export function FileUploadDrawerContent({
    fileItems,
    isExpanded,
    setExpanded,
    onClear,
    onRetry,
}: FileUploadDrawerContentProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const uploadingCount = fileItems.filter(
        (item) => item.status === FileUploadStatus.UPLOADING,
    ).length;
    const pendingCount = fileItems.filter(
        (item) => item.status === FileUploadStatus.PENDING,
    ).length;
    const completedCount = fileItems.filter(
        (item) => item.status === FileUploadStatus.COMPLETED,
    ).length;
    const totalCount = fileItems.length;

    const awaitingBackendCount = fileItems.filter(
        isFileUploadItemAwaitingBackendTerminal,
    ).length;

    const hasActiveUploads = uploadingCount > 0 || pendingCount > 0;
    const allComplete = !hasActiveUploads && awaitingBackendCount === 0;

    const uploadTitle = hasActiveUploads
        ? t('statusDrawer.uploadingProgress', {
              remaining: uploadingCount + pendingCount,
              total: totalCount,
          })
        : awaitingBackendCount > 0
          ? t('statusDrawer.preparingProgress', {
                remaining: awaitingBackendCount,
                total: totalCount,
            })
          : t('statusDrawer.uploadedCount', { count: completedCount });

    return (
        <StatusDrawerLayout
            title={uploadTitle}
            isExpanded={isExpanded}
            allComplete={allComplete}
            onToggleExpanded={() => setExpanded(!isExpanded)}
            onClose={onClear}
        >
            {fileItems.map((item) => (
                <FileSourceStatusDrawerItem
                    key={item.id}
                    item={item}
                    onRetry={onRetry}
                />
            ))}
        </StatusDrawerLayout>
    );
}
